/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { Platform } from 'react-native';
import {
  CurrencyProvider,
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
  type SupportedCurrencies,
  useCurrency,
} from '@airasia-rn/ui/config';
import type { HotelConfig } from '../types';
import { parseHotelDeepLinkQueryParams } from '../utils/deeplink';

type HotelConfigProviderProps = HotelConfig & {
  children: ReactNode;
};

type HotelConfigContextType = {
  config: HotelConfig;
  updateConfig: (updates: Partial<HotelConfig>) => void;
};

export const HotelConfigContext = createContext<
  HotelConfigContextType | undefined
>(undefined);

const getDeeplinkCurrency = (
  deeplink?: string,
): SupportedCurrencies | undefined => {
  const currencyParam = parseHotelDeepLinkQueryParams(
    deeplink ?? null,
  ).currency;
  const currency = (
    Array.isArray(currencyParam) ? currencyParam[0] : currencyParam
  )?.toUpperCase();

  return currency && currency in SUPPORTED_CURRENCIES
    ? (currency as SupportedCurrencies)
    : undefined;
};

export const HotelConfigProvider: React.FC<HotelConfigProviderProps> = ({
  children,
  ...initialConfig
}) => {
  const { currency: appCurrency } = useCurrency();
  const deeplinkCurrency = getDeeplinkCurrency(initialConfig.deeplink);

  const [config, setConfig] = useState<HotelConfig>(() => ({
    ...initialConfig,
    ...(deeplinkCurrency ? { currency: deeplinkCurrency } : {}),
  }));

  // The Super App can deliver a new deeplink while Hotels remains mounted.
  // Keep only this externally-driven config value synchronized; other config
  // fields may be changed locally through updateConfig.
  useEffect(() => {
    setConfig((currentConfig) =>
      currentConfig.deeplink === initialConfig.deeplink
        ? currentConfig
        : {
            ...currentConfig,
            deeplink: initialConfig.deeplink,
            ...(deeplinkCurrency ? { currency: deeplinkCurrency } : {}),
          },
    );
  }, [deeplinkCurrency, initialConfig.deeplink]);

  const updateConfig = useCallback((updates: Partial<HotelConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const value = useMemo(
    () => ({
      config,
      updateConfig,
    }),
    [config, updateConfig],
  );

  const handleCurrencyChange = useCallback(
    (currency: SupportedCurrencies) => {
      updateConfig({ currency });
    },
    [updateConfig],
  );

  const content =
    Platform.OS === 'web' ? (
      children
    ) : (
      <CurrencyProvider
        initialCurrency={
          (config.deeplink !== initialConfig.deeplink
            ? deeplinkCurrency
            : undefined) ??
          config.currency ??
          appCurrency ??
          DEFAULT_CURRENCY
        }
        onCurrencyChange={handleCurrencyChange}
      >
        {children}
      </CurrencyProvider>
    );

  return (
    <HotelConfigContext.Provider value={value}>
      {content}
    </HotelConfigContext.Provider>
  );
};

export const useOptionalHotelConfig = ():
  | HotelConfigContextType
  | undefined => {
  return useContext(HotelConfigContext);
};

export const useHotelConfig = (): HotelConfigContextType => {
  const ctx = useContext(HotelConfigContext);
  if (!ctx) {
    throw new Error('useHotelConfig must be used inside HotelConfigProvider');
  }
  return ctx;
};
